import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AdTypeBadge } from './StatusBadge'
import type { TopCampaign } from '@/api/metrics'

interface TopCampaignsTableProps {
  campaigns: TopCampaign[]
  loading?: boolean
}

type SortField = 'spend' | 'sales' | 'acos' | 'roas' | 'impressions' | 'clicks'

export default function TopCampaignsTable({ campaigns, loading = false }: TopCampaignsTableProps) {
  const navigate = useNavigate()
  const [sortField, setSortField] = useState<SortField>('spend')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortField(field); setSortDir('desc') }
  }

  const sorted = [...campaigns].sort((a, b) => {
    const aVal = a[sortField] ?? 0
    const bVal = b[sortField] ?? 0
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal
  })

  if (loading) {
    return (
      <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
        <h3 className="text-sm font-semibold mb-3">Top Kampagnen</h3>
        <div className="h-[200px] flex items-center justify-center text-sm text-[var(--muted-foreground)]">Lade...</div>
      </div>
    )
  }

  function SortHeader({ field, children, align = 'right' }: { field: SortField; children: React.ReactNode; align?: 'left' | 'right' }) {
    return (
      <th className={cn('px-2 py-1.5 font-medium cursor-pointer select-none hover:text-[var(--foreground)]', align === 'right' ? 'text-right' : 'text-left')}
        onClick={() => handleSort(field)}>
        <span className="inline-flex items-center gap-0.5">
          {children}
          <ArrowUpDown className={cn('h-3 w-3', sortField === field ? 'text-[var(--foreground)]' : 'text-[var(--muted-foreground)]')} />
        </span>
      </th>
    )
  }

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
      <h3 className="text-sm font-semibold mb-3">Top 10 Kampagnen</h3>
      {sorted.length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">Keine Daten</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-[var(--muted-foreground)]">
                <th className="px-2 py-1.5 text-left font-medium">#</th>
                <th className="px-2 py-1.5 text-left font-medium">Kampagne</th>
                <th className="px-2 py-1.5 text-left font-medium">Typ</th>
                <SortHeader field="spend">Spend</SortHeader>
                <SortHeader field="sales">Sales</SortHeader>
                <SortHeader field="acos">ACoS</SortHeader>
                <SortHeader field="roas">ROAS</SortHeader>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, idx) => (
                <tr key={c.campaignId} className="border-t border-[var(--border)] hover:bg-[var(--muted)]/50 cursor-pointer"
                  onClick={() => navigate(`/campaigns/${c.campaignId}`)}>
                  <td className="px-2 py-1.5 text-[var(--muted-foreground)]">{idx + 1}</td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate font-medium" title={c.name}>{c.name}</td>
                  <td className="px-2 py-1.5"><AdTypeBadge adType={c.adType} /></td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.spend.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.sales.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.acos.toFixed(1)}%</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{c.roas.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
